/**
 * User Management Component
 * 
 * Main component for managing users/staff. Handles CRUD operations,
 * URL-based filtering, and coordinates between list and form views.
 * 
 * @component
 * @example
 * return <UserManagement />
 */

import React, { useEffect, useState } from "react";
import { message } from "antd";
import UserList from "./user-list";
import UserForm from "./user-form";
import CustomContainer from "../common/CustomContainer";
import UserService from "../../services/user-service";
import { getFacilities } from "../../utils/apiClient";
import { formatPhoneNumber, formatPhoneNumberForEdit } from "../../utils/helpers/format-phone-number";
import { useURLFiltering, useCRUDOperations } from "../../custom-hooks";

/**
 * User Management Main Component
 */
const UserManagement = () => {
  const [facilities, setFacilities] = useState([]);

  // Use custom hooks for common functionality
  const {
    selectedFilters,
    updateFilters,
    getAPIParams
  } = useURLFiltering('user');

  const {
    data: users,
    loading,
    renderType,
    initialValues: selectedUser,
    fetchData,
    refreshData,
    handleSubmit: handleCRUDSubmit,
    handleEdit: handleCRUDEdit,
    handleAdd,
    handleCancel,
    setRenderType
  } = useCRUDOperations(UserService, 'user');

  /**
   * Fetches facilities for user form
   */
  const fetchFacilities = async () => {
    try {
      const response = await getFacilities();
      setFacilities(response);
    } catch (error) {
      console.error("Error fetching facilities:", error);
      message.error(
        error.response?.data?.message ||
          "An error occurred while fetching facilities"
      );
    }
  };

  /**
   * Handles filter changes with API integration
   * 
   * @param {Object} newFilters - New filter values
   */
  const handleFiltersChange = (newFilters) => {
    try {
      updateFilters(newFilters); // This will trigger useEffect to call fetchData
    } catch (error) {
      console.error('Error applying filters:', error);
      message.error('Failed to apply filters. Please try again.');
    }
  };

  /**
   * Refreshes user data with current filters
   * 
   * @returns {Promise<Array>} Refreshed user data
   */
  const handleRefresh = async () => {
    const apiParams = getAPIParams(selectedFilters);
    return await refreshData(apiParams);
  };

  /**
   * Handles adding a new user
   */
  const handleAddNew = () => {
    handleAdd();
  };

  /**
   * Handles editing a user
   * 
   * @param {Object} user - User to edit
   */
  const handleEdit = (user) => {
    const modifiedUser = {
      ...user,
      role: user.role[0] || '',
      phoneNumber: formatPhoneNumberForEdit(user.phoneNumber) || '',
    };
    handleCRUDEdit(modifiedUser);
  };

  /**
   * Handles form close/cancel
   */
  const handleFormClose = () => {
    handleCancel();
  };

  /**
   * Handles form submission for create/update
   * 
   * @param {Object} values - Form values (may be partial for edits)
   */
  const handleSubmit = async (values) => {
    console.log(values, "valuesDebugger");
    const customerId = sessionStorage.getItem("customerId");
    const isEdit = !!selectedUser;
    
    const processedValues = {
      ...values,
      ...(values.phoneNumber && { phoneNumber: formatPhoneNumber(values.phoneNumber) })
    };
    
    const userData = UserService.formatForSubmission(
      processedValues, 
      customerId, 
      isEdit
    );

    const result = await handleCRUDSubmit(userData, handleRefresh);
    return result;
  };

  // Initialize data on component mount
  useEffect(() => {
    const apiParams = getAPIParams(selectedFilters);
    fetchFacilities();
    fetchData(apiParams);
  }, [selectedFilters, fetchData, getAPIParams]);

  return (
    <CustomContainer containerWidth={renderType === "list" ? "90%" : "80%"}>
      {renderType === "list" ? (
        <UserList
          onAddNew={handleAddNew}
          onEdit={handleEdit}
          facilities={facilities}
          users={users}
          loading={loading}
          onRefresh={handleRefresh}
          selectedFilters={selectedFilters}
          onFiltersChange={handleFiltersChange}
        />
      ) : (
        <UserForm
          user={selectedUser}
          onClose={handleFormClose}
          setRenderType={setRenderType}
          renderType={renderType}
          facilities={facilities}
          handleSubmit={handleSubmit}
        />
      )}
    </CustomContainer>
  );
};

export default UserManagement;